import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { formatDateForDisplay } from '../utils/timezone';

// Custom styles for the dashboard
const styles = {
  dashboardContainer: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: '2rem'
  },
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: '1.5rem',
    fontFamily: "'Inter', sans-serif"
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
    }
  },
  statCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)'
    }
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem'
  },
  activityItem: {
    padding: '1rem',
    borderRadius: '8px',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#f8f9fa'
    }
  },
  chartContainer: {
    position: 'relative',
    margin: 'auto',
    height: '100%'
  },
  badge: {
    padding: '0.5em 0.8em',
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '0.75rem'
  }
};

// Chart.js configuration
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Custom chart options
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        boxWidth: 12,
        padding: 15,
        font: {
          family: "'Inter', sans-serif",
          size: 12
        }
      }
    },
    tooltip: {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      titleColor: '#2c3e50',
      bodyColor: '#2c3e50',
      borderColor: '#e9ecef',
      borderWidth: 1,
      padding: 12,
      boxPadding: 6,
      usePointStyle: true,
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== undefined) {
            label += new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD'
            }).format(context.parsed.y);
          }
          return label;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
        drawBorder: false
      },
      ticks: {
        font: {
          family: "'Inter', sans-serif",
          size: 11
        }
      }
    },
    x: {
      grid: {
        display: false
      },
      ticks: {
        font: {
          family: "'Inter', sans-serif",
          size: 11
        }
      }
    }
  }
};

const StatCard = ({ title, value, icon, color, trend, trendValue }) => {
  const colorMap = {
    primary: { bg: '#e3f2fd', text: '#1976d2' },
    success: { bg: '#e8f5e9', text: '#2e7d32' },
    info: { bg: '#e0f7fa', text: '#0288d1' },
    warning: { bg: '#fff3e0', text: '#f57c00' },
    danger: { bg: '#ffebee', text: '#d32f2f' }
  };

  const colorStyle = colorMap[color] || colorMap.primary;

  return (
    <div className="card h-100" style={styles.statCard}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h6 className="text-muted mb-2" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
              {title}
            </h6>
            <h3 className="mb-0" style={{ fontSize: '1.75rem', fontWeight: '600', color: '#2c3e50' }}>
              {value}
            </h3>
            {trend && (
              <small 
                className={`d-flex align-items-center mt-2`}
                style={{ 
                  color: trend === 'up' ? '#2e7d32' : '#d32f2f',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                <i className={`bi bi-arrow-${trend}-right me-1`}></i>
                {trendValue}
              </small>
            )}
          </div>
          <div 
            style={{
              ...styles.statIcon,
              backgroundColor: colorStyle.bg,
              color: colorStyle.text
            }}
          >
            <i className={`bi bi-${icon}`}></i>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const api = useApi();
  const { user, loading: userLoading } = useUser();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeCustomers: 0,
    availableMachines: 0,
    pendingTickets: 0,
    pendingTicketsTotal: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [revenueData, setRevenueData] = useState({
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: [
        'rgba(46, 125, 50, 0.9)',
        'rgba(25, 118, 210, 0.9)',
        'rgba(245, 124, 0, 0.9)'
      ],
      borderWidth: 0,
      borderRadius: 4
    }]
  });
  const [machineUtilizationData, setMachineUtilizationData] = useState({
    labels: [],
    datasets: [{
      label: 'Utilization %',
      data: [],
      backgroundColor: 'rgba(25, 118, 210, 0.8)',
      borderRadius: 4,
      borderWidth: 0
    }]
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userLoading) return;

    if (user?.role === 'foreman') {
      setLoading(false);
      setError(null);
      return;
    }

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch dashboard statistics
        const statsResponse = await api({
          method: 'get',
          url: '/dashboard/stats'
        });

        // Fetch recent activities //
        const activitiesResponse = await api({
          method: 'get',
          url: '/dashboard/recent-activity'
        });

        // Fetch revenue data
        const revenueResponse = await api({
          method: 'get',
          url: '/dashboard/revenue'
        });

        // Fetch machine utilization data
        const machineUtilizationResponse = await api({
          method: 'get',
          url: '/dashboard/machine-utilization'
        });

        // Update state with fetched data
        if (statsResponse.data.success) {
          // Map backend fields to frontend expected fields
          const statsData = statsResponse.data.data;
          setStats(prev => ({
            ...prev,
            totalJobs: statsData.totalJobs ?? 0,
            activeCustomers: statsData.activeCustomers ?? 0,
            availableMachines: statsData.availableMachines ?? 0,
            pendingTickets: statsData.pendingItems ?? 0,
            pendingTicketsTotal: statsData.pendingItemsTotal ?? 0
          }));
        }

        if (activitiesResponse.data.success) {
          setRecentActivity(activitiesResponse.data.data);
        }

        if (revenueResponse.data.success) {
          const revenueData = revenueResponse.data.data;
          setRevenueData(prev => ({
            ...prev,
            labels: revenueData.map(item => item.period),
            datasets: [{
              ...prev.datasets[0],
              data: revenueData.map(item => item.revenue)
            }]
          }));
        }

        if (machineUtilizationResponse.data.success) {
          const machineUtilizationData = machineUtilizationResponse.data.data;
          setMachineUtilizationData(prev => ({
            ...prev,
            labels: machineUtilizationData.map(item => item.name),
            datasets: [{
              ...prev.datasets[0],
              data: machineUtilizationData.map(item => item.totalHours)
            }]
          }));
        }

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.response?.data?.message || 'Error loading dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [api, user?.role, userLoading]);

  if (userLoading) {
    return (
      <div style={styles.dashboardContainer}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (user?.role === 'foreman') {
    return (
      <div style={styles.dashboardContainer}>
        <h1 style={styles.pageTitle}>Welcome</h1>
        <p className="text-muted mb-4" style={{ maxWidth: 560 }}>
          You can work with customers, jobs, tickets, and machines assigned to your role. Revenue and billing reports are not available here.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => navigate('/tickets')}>
          Go to Tickets
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.dashboardContainer}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.dashboardContainer}>
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  const getActivityIcon = (type) => {
    const iconMap = {
      job: { icon: 'bi-briefcase', color: '#1976d2' },
      ticket: { icon: 'bi-receipt', color: '#2e7d32' },
      machine: { icon: 'bi-truck', color: '#f57c00' },
      customer: { icon: 'bi-person', color: '#7b1fa2' },
      employee: { icon: 'bi-person-badge', color: '#9c27b0' }
    };
    return iconMap[type] || { icon: 'bi-circle', color: '#757575' };
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'In Progress': { bg: '#e3f2fd', text: '#1976d2' },
      'Pending': { bg: '#fff3e0', text: '#f57c00' },
      'Completed': { bg: '#e8f5e9', text: '#2e7d32' },
      'Maintenance Required': { bg: '#ffebee', text: '#d32f2f' },
      'Active': { bg: '#e8f5e9', text: '#2e7d32' }
    };
    return statusMap[status] || { bg: '#f5f5f5', text: '#757575' };
  };

  return (
    <div style={styles.dashboardContainer}>
      <h2 style={styles.pageTitle}>Dashboard</h2>

      {/* Stats Cards Row with Totals */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <StatCard
            title="Total Jobs"
            value={stats.totalJobs}
            icon="briefcase"
            color="primary"
            trend="up"
            trendValue={`${Math.round((stats.totalJobs / 200) * 100)}% of capacity`}
          />
        </div>
        <div className="col-md-3">
          <StatCard
            title="Total Customers"
            value={stats.activeCustomers}
            icon="people"
            color="success"
            trend="up"
            trendValue={`${Math.round((stats.activeCustomers / 60) * 100)}% of target`}
          />
        </div>
        <div className="col-md-3">
          <StatCard
            title="Total Machines"
            value={stats.availableMachines}
            icon="truck"
            color="info"
            trend="up"
            trendValue={`${Math.round((stats.availableMachines / 40) * 100)}% utilization`}
          />
        </div>
        <div className="col-md-3">
          <StatCard
            title="Pending Tickets"
            value={stats.pendingTickets}
            icon="receipt"
            color="warning"
            trend="up"
            trendValue={`$${stats.pendingTicketsTotal?.toLocaleString()} total`}
          />
        </div>
      </div>

      <div className="row g-3">
        {/* Charts Section */}
        <div className="col-md-8">
          <div className="row g-3">
            {/* Job Status Chart */}
            <div className="col-md-6">
              <div className="card h-100" style={styles.card}>
                <div className="card-body">
                  <h6 className="card-title mb-3" style={{ fontSize: '1rem', fontWeight: '600', color: '#2c3e50' }}>
                    Job Status
                  </h6>
                  <div style={{ height: '250px' }}>
                    <Doughnut 
                      data={revenueData} 
                      options={{
                        ...chartOptions,
                        cutout: '70%',
                        plugins: {
                          ...chartOptions.plugins,
                          legend: {
                            ...chartOptions.plugins.legend,
                            position: 'bottom'
                          }
                        }
                      }} 
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Machine Utilization Chart */}
            <div className="col-md-6">
              <div className="card h-100" style={styles.card}>
                <div className="card-body">
                  <h6 className="card-title mb-3" style={{ fontSize: '1rem', fontWeight: '600', color: '#2c3e50' }}>
                    Machine Utilization
                  </h6>
                  <div style={{ height: '250px' }}>
                    <Bar data={machineUtilizationData} options={chartOptions} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity Section */}
        <div className="col-md-4">
          <div className="card h-100" style={styles.card}>
            <div className="card-body">
              <h6 className="card-title mb-3" style={{ fontSize: '1rem', fontWeight: '600', color: '#2c3e50' }}>
                Recent Activity
              </h6>
              <div className="activity-list">
                {recentActivity.map(activity => {
                  const iconStyle = getActivityIcon(activity.type);
                  const statusStyle = getStatusBadgeClass(activity.status);
                  return (
                    <div key={activity.id} className="activity-item mb-3">
                      <div className="d-flex">
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: '36px',
                            height: '36px',
                            backgroundColor: `${iconStyle.color}15`,
                            color: iconStyle.color
                          }}
                        >
                          <i className={`bi ${iconStyle.icon}`}></i>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 
                                className="mb-1"
                                style={{ 
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  color: '#2c3e50'
                                }}
                              >
                                {activity.title}
                              </h6>
                              {activity.customer && (
                                <small 
                                  className="d-block"
                                  style={{ 
                                    color: '#6c757d',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {activity.customer}
                                </small>
                              )}
                            </div>
                            <span 
                              style={{
                                ...styles.badge,
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.text
                              }}
                            >
                              {activity.status}
                            </span>
                          </div>
                          <small 
                            style={{ 
                              color: '#6c757d',
                              fontSize: '0.75rem',
                              display: 'block',
                              marginTop: '0.25rem'
                            }}
                          >
                            {formatDateForDisplay(activity.date)}
                          </small>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 